import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Star, TrendingUp, AlertCircle, CheckCircle } from 'lucide-react';
import BackButton from '../components/BackButton';
import StartSidebar from '../components/StartSidebar';
import ProgramDetail from './ProgramDetail';
import { db } from '../firebase';
import { doc, onSnapshot, collection, query, where, getDocs, getDoc } from 'firebase/firestore';
import { getCrmCompanyLogoDisplayUrl } from '../services/firestoreService';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';

const LEVER_DEPARTMENTS = [
  { id: 'sales', name: 'Sales', color: 'red' },
  { id: 'operations', name: 'Operations', color: 'blue' },
  { id: 'finance', name: 'Finance', color: 'green' },
  { id: 'hr', name: 'HR', color: 'yellow' },
  { id: 'technology', name: 'Technology', color: 'purple' },
  { id: 'marketing', name: 'Marketing', color: 'orange' }
];
const departmentPillClasses = {
  red: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-700',
  blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-700',
  green: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-700',
  yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700',
  purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200 dark:border-purple-700',
  orange: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 border-orange-200 dark:border-orange-700'
};

const AssessmentAnalysisOverview = () => {
  const { leadId } = useParams();
  const { currentUser } = useAuth();
  const { darkMode } = useTheme();
  const [lead, setLead] = useState(null);
  // The route param may be an `email:` sentinel; this holds the real account doc id once resolved.
  // Anything account-scoped (e.g. published bid lookups downstream) must use this, not the param.
  const [resolvedLeadId, setResolvedLeadId] = useState(leadId?.startsWith('email:') ? null : leadId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadLeadData = async () => {
      if (!leadId) {
        setError('Lead ID is required');
        setLoading(false);
        return;
      }

      let actualLeadId = leadId;

      if (leadId.startsWith('email:')) {
        const email = leadId.replace('email:', '');
        try {
          const normalizedEmail = email.toLowerCase().trim();
          const searchQueries = [
            query(collection(db, 'accounts'), where('contact.email', '==', normalizedEmail), where('type', '==', 'Leads')),
            query(collection(db, 'accounts'), where('email', '==', normalizedEmail), where('type', '==', 'Leads')),
            query(collection(db, 'accounts'), where('contact.email', '==', normalizedEmail)),
            query(collection(db, 'accounts'), where('email', '==', normalizedEmail))
          ];

          let foundAccountId = null;
          for (const searchQuery of searchQueries) {
            try {
              const accountsSnapshot = await getDocs(searchQuery);
              if (!accountsSnapshot.empty) {
                foundAccountId = accountsSnapshot.docs[0].id;
                break;
              }
            } catch (err) {
              // Continue to next query
            }
          }

          if (foundAccountId) {
            actualLeadId = foundAccountId;
            setResolvedLeadId(foundAccountId);
            window.history.replaceState(null, '', `/start/assessment-analysis/${foundAccountId}`);
          } else {
            setError('Account not found. Please ensure you have completed your assessment.');
            setLoading(false);
            return;
          }
        } catch (err) {
          console.error('Error finding account by email:', err);
          setError('Unable to find your account. Please contact support.');
          setLoading(false);
          return;
        }
      }

      const leadDocRef = doc(db, 'accounts', actualLeadId);
      const unsubscribe = onSnapshot(leadDocRef,
        (docSnapshot) => {
          try {
            if (!docSnapshot.exists()) {
              if (currentUser?.email) {
                const findAccountByEmail = async () => {
                  try {
                    const normalizedEmail = currentUser.email.toLowerCase().trim();
                    const searchQueries = [
                      query(collection(db, 'accounts'), where('contact.email', '==', normalizedEmail), where('type', '==', 'Leads')),
                      query(collection(db, 'accounts'), where('email', '==', normalizedEmail), where('type', '==', 'Leads')),
                      query(collection(db, 'accounts'), where('contact.email', '==', normalizedEmail)),
                      query(collection(db, 'accounts'), where('email', '==', normalizedEmail))
                    ];

                    for (const searchQuery of searchQueries) {
                      try {
                        const accountsSnapshot = await getDocs(searchQuery);
                        if (!accountsSnapshot.empty) {
                          const foundAccountId = accountsSnapshot.docs[0].id;
                          const foundAccountDoc = await getDoc(doc(db, 'accounts', foundAccountId));
                          if (foundAccountDoc.exists()) {
                            const data = foundAccountDoc.data();
                            setLead(data);
                            setResolvedLeadId(foundAccountId);
                            setLoading(false);
                            setError(null);
                            window.history.replaceState(null, '', `/start/assessment-analysis/${foundAccountId}`);
                            return;
                          }
                        }
                      } catch (err) {
                        // Continue
                      }
                    }
                  } catch (err) {
                    console.error('Error in fallback email search:', err);
                  }
                  setError('Lead not found');
                  setLoading(false);
                };
                findAccountByEmail();
                return;
              }
              throw new Error('Lead not found');
            }

            const data = docSnapshot.data();
            setLead(data);
            setResolvedLeadId(actualLeadId);
            setLoading(false);
            setError(null);
          } catch (err) {
            console.error('Error loading lead data:', err);
            setError(err.message);
            setLoading(false);
          }
        },
        (err) => {
          console.error('Error in onSnapshot:', err);
          setError('Failed to load lead data');
          setLoading(false);
        }
      );

      return () => unsubscribe();
    };

    loadLeadData();
  }, [leadId, currentUser]);

  const salesData = {
    ...(lead?.businessAssessment || {}),
    ...(lead?.salesData || {})
  };

  const calculateBBARating = useMemo(() => {
    if (!salesData || Object.keys(salesData).length === 0) return 0;
    let score = 0;
    let factors = 0;
    if (salesData.salesRating) { score += salesData.salesRating; factors += 1; }
    if (salesData.closeRatio !== undefined && salesData.closeRatio !== null) {
      if (salesData.closeRatio < 30) score += 2;
      else if (salesData.closeRatio < 50) score += 4;
      else score += 5;
      factors += 1;
    }
    if (salesData.referralBusinessPercentage !== undefined && salesData.referralBusinessPercentage !== null) {
      const referralScore = Math.min(5, Math.max(1, Math.round(salesData.referralBusinessPercentage / 20)));
      score += referralScore;
      factors += 1;
    }
    if (salesData.repeatCustomerPercentage !== undefined && salesData.repeatCustomerPercentage !== null) {
      const repeatScore = salesData.repeatCustomerPercentage >= 2 && salesData.repeatCustomerPercentage <= 8 ? 5 : salesData.repeatCustomerPercentage < 2 ? 2 : 3;
      score += repeatScore;
      factors += 1;
    }
    return factors > 0 ? Math.round(score / factors) : 0;
  }, [salesData]);

  const ownerRating = salesData.salesRating || 0;
  const bbaRating = calculateBBARating;

  const operationsData = {
    ...(lead?.businessAssessment || {}),
    ...(lead?.operationsData || {})
  };

  const calculateOperationsBBARating = useMemo(() => {
    if (!operationsData || Object.keys(operationsData).length === 0) return 0;
    let score = 0;
    let factors = 0;
    if (operationsData.operationsRating) { score += operationsData.operationsRating; factors += 1; }
    if (operationsData.hasSOPs !== undefined && operationsData.hasSOPs !== null) {
      score += operationsData.hasSOPs === 'yes' ? 5 : 1;
      factors += 1;
    }
    if (operationsData.hasJobDescriptions !== undefined && operationsData.hasJobDescriptions !== null) {
      score += operationsData.hasJobDescriptions === 'yes' ? 5 : 1;
      factors += 1;
    }
    if (operationsData.hasKPIs !== undefined && operationsData.hasKPIs !== null) {
      score += operationsData.hasKPIs === 'yes' ? 5 : 1;
      factors += 1;
    }
    if (operationsData.companyCultureRating && operationsData.companyCultureRating > 0) {
      score += operationsData.companyCultureRating;
      factors += 1;
    }
    if (operationsData.teamGrade) {
      const gradeScores = { 'A': 5, 'B': 4, 'C': 3, 'D': 2, 'F': 1 };
      score += gradeScores[operationsData.teamGrade.toUpperCase()] || 3;
      factors += 1;
    }
    return factors > 0 ? Math.round(score / factors) : 0;
  }, [operationsData]);

  const operationsOwnerRating = operationsData.operationsRating || 0;
  const operationsBBARating = calculateOperationsBBARating;

  const financeData = {
    ...(lead?.businessAssessment || {}),
    ...(lead?.financeData || {})
  };

  const calculateFinanceBBARating = useMemo(() => {
    if (!financeData || Object.keys(financeData).length === 0) return 0;
    let score = 0;
    let factors = 0;
    if (financeData.financeRating) { score += financeData.financeRating; factors += 1; }
    if (financeData.hasMonthlyReconciliation !== undefined && financeData.hasMonthlyReconciliation !== null) {
      score += financeData.hasMonthlyReconciliation === 'yes' ? 5 : 1;
      factors += 1;
    }
    if (financeData.hasCashFlowProjection !== undefined && financeData.hasCashFlowProjection !== null) {
      score += financeData.hasCashFlowProjection === 'yes' ? 5 : 1;
      factors += 1;
    }
    if (financeData.hasProjectBudgetsVsActuals !== undefined && financeData.hasProjectBudgetsVsActuals !== null) {
      score += financeData.hasProjectBudgetsVsActuals === 'yes' ? 5 : 1;
      factors += 1;
    }
    if (financeData.hasDebt !== undefined && financeData.hasDebt !== null) {
      score += financeData.hasDebt === 'yes' ? 1 : 5;
      factors += 1;
    }
    if (financeData.hasFinancialPartners !== undefined && financeData.hasFinancialPartners !== null) {
      score += financeData.hasFinancialPartners === 'yes' ? 5 : 1;
      factors += 1;
    }
    if (financeData.hasCreditLine !== undefined && financeData.hasCreditLine !== null) {
      score += financeData.hasCreditLine === 'yes' ? 5 : 1;
      factors += 1;
    }
    const unencumberedCash = parseFloat(financeData.unencumberedCash || 0);
    const currentYearRevenue = parseFloat(salesData.currentYearProjection || salesData.revenueData?.currentYearProjection || 0);
    if (unencumberedCash > 0 && currentYearRevenue > 0) {
      const cashPercentage = (unencumberedCash / currentYearRevenue) * 100;
      if (cashPercentage < 5) score += 1;
      else if (cashPercentage <= 10) score += 3;
      else score += 5;
      factors += 1;
    }
    const gpm = parseFloat(financeData.grossProfitMargin || 0);
    if (gpm > 0) {
      if (gpm < 30) score += 1;
      else if (gpm <= 40) score += 3;
      else score += 5;
      factors += 1;
    }
    const npm = parseFloat(financeData.netProfitMargin || 0);
    if (npm > 0) {
      if (npm < 5) score += 1;
      else if (npm <= 10) score += 3;
      else score += 5;
      factors += 1;
    }
    return factors > 0 ? Math.round(score / factors) : 0;
  }, [financeData, salesData]);

  const financeOwnerRating = financeData.financeRating || financeData.rating || 0;
  const financeBBARating = calculateFinanceBBARating;

  const marketingData = {
    ...(lead?.businessAssessment || {}),
    ...(lead?.marketingData || {})
  };

  const calculateMarketingBBARating = useMemo(() => {
    if (!marketingData || Object.keys(marketingData).length === 0) return 0;
    let score = 0;
    let factors = 0;
    if (marketingData.marketingRating || marketingData.rating) {
      score += marketingData.marketingRating || marketingData.rating;
      factors += 1;
    }
    if (marketingData.monthlyMarketingBudget || marketingData.annualMarketingBudget) {
      score += 4;
      factors += 1;
    }
    if (marketingData.runningDigitalAds !== undefined && marketingData.runningDigitalAds !== null) {
      score += marketingData.runningDigitalAds === 'yes' ? 5 : 1;
      factors += 1;
    }
    if (marketingData.digitalAdPlatforms && Array.isArray(marketingData.digitalAdPlatforms) && marketingData.digitalAdPlatforms.length > 0) {
      score += Math.min(5, marketingData.digitalAdPlatforms.length);
      factors += 1;
    }
    if (marketingData.websiteManager) {
      score += 4;
      factors += 1;
    }
    return factors > 0 ? Math.round(score / factors) : 0;
  }, [marketingData]);

  const marketingOwnerRating = marketingData.marketingRating || marketingData.rating || 0;
  const marketingBBARating = calculateMarketingBBARating;

  const techData = {
    ...(lead?.businessAssessment || {}),
    ...(lead?.techData || {})
  };

  const calculateTechBBARating = useMemo(() => {
    if (!techData || Object.keys(techData).length === 0) return 0;
    let score = 0;
    let factors = 0;
    if (techData.techRating || techData.rating) {
      score += techData.techRating || techData.rating;
      factors += 1;
    }
    const crm = techData.whatCRM || '';
    if (crm) {
      const crmLower = crm.toLowerCase();
      if (crmLower.includes('jobtread') || crmLower.includes('buildertrend')) score += 5;
      else if (crmLower.includes('procore') || crmLower.includes('hubspot') || crmLower === 'other') score += 3;
      else if (crmLower === 'none' || crmLower === '') score += 1;
      else score += 3;
      factors += 1;
    }
    const crmOpt = parseFloat(techData.crmOptimization || 0);
    if (crmOpt > 0) {
      if (crmOpt < 50) score += 1;
      else if (crmOpt <= 75) score += 3;
      else score += 5;
      factors += 1;
    }
    if (techData.hasAutomations !== undefined && techData.hasAutomations !== null) {
      score += techData.hasAutomations === 'yes' ? 5 : 1;
      factors += 1;
    }
    if (techData.hasAITools !== undefined && techData.hasAITools !== null) {
      score += techData.hasAITools === 'yes' ? 5 : 1;
      factors += 1;
    }
    const hasPlugins = techData.hasWebsitePlugins || techData.hasCustomWebsitePlugins;
    if (hasPlugins !== undefined && hasPlugins !== null) {
      score += hasPlugins === 'yes' ? 5 : 1;
      factors += 1;
    }
    return factors > 0 ? Math.round(score / factors) : 0;
  }, [techData]);

  const techOwnerRating = techData.techRating || techData.rating || 0;
  const techBBARating = calculateTechBBARating;

  const swotData = useMemo(() => {
    const strengths = [];
    const weaknesses = [];
    // The first opportunity is always the PD/PM Model implementation.
    const opportunities = ['Implement PD/PM Model'];
    const isNo = (value) => {
      if (value === null || value === undefined || value === '') return true;
      if (value === 'no' || value === false) return true;
      return false;
    };
    const isYes = (value) => value === 'yes' || value === true;

    if (operationsData.hasSOPs !== undefined && operationsData.hasSOPs !== null && operationsData.hasSOPs !== '') {
      if (isYes(operationsData.hasSOPs)) strengths.push('Has Standard Operating Procedures (SOPs)');
      else if (isNo(operationsData.hasSOPs)) {
        weaknesses.push('No Systems or Processes');
        opportunities.push('Implement Standard Operating Procedures (SOPs)');
      }
    }
    if (operationsData.hasJobDescriptions !== undefined && operationsData.hasJobDescriptions !== null && operationsData.hasJobDescriptions !== '') {
      if (isYes(operationsData.hasJobDescriptions)) strengths.push('Has Job Descriptions');
      else if (isNo(operationsData.hasJobDescriptions)) {
        weaknesses.push('No Job Descriptions');
        opportunities.push('Create Job Descriptions');
      }
    }
    if (operationsData.hasKPIs !== undefined && operationsData.hasKPIs !== null && operationsData.hasKPIs !== '') {
      if (isYes(operationsData.hasKPIs)) strengths.push('Has Key Performance Indicators (KPIs)');
      else if (isNo(operationsData.hasKPIs)) {
        weaknesses.push('No Key Performance Indicators (KPIs)');
        opportunities.push('Implement Key Performance Indicators (KPIs)');
      }
    }
    if (operationsData.companyCultureRating >= 4) strengths.push('Strong Company Culture');
    else if (operationsData.companyCultureRating <= 2 && operationsData.companyCultureRating > 0) weaknesses.push('Weak Company Culture');
    if (operationsData.teamGrade && ['A', 'B'].includes(operationsData.teamGrade.toUpperCase())) strengths.push(`Strong Team (Grade ${operationsData.teamGrade.toUpperCase()})`);
    else if (operationsData.teamGrade && ['D', 'F'].includes(operationsData.teamGrade.toUpperCase())) weaknesses.push(`Weak Team (Grade ${operationsData.teamGrade.toUpperCase()})`);
    const repeatCustomerPercentage = parseFloat(salesData.repeatCustomerPercentage || 0);
    if (repeatCustomerPercentage > 0 && repeatCustomerPercentage < 5) weaknesses.push('Low Repeat Customer Percentage');
    const referralPercentage = parseFloat(salesData.referralBusinessPercentage || 0);
    if (referralPercentage > 70) strengths.push('Strong Referral Based Business');
    const closeRatioValue = parseFloat(salesData.closeRatio);
    if (!isNaN(closeRatioValue) && closeRatioValue > 0) {
      if (closeRatioValue < 50) weaknesses.push(`Low Close Ratio (${closeRatioValue}%)`);
      else if (closeRatioValue > 60) strengths.push(`Strong Close Ratio (${closeRatioValue}%)`);
    }
    // Overhead / labor burden / project runway: only when the owner is aware of all three.
    if (isYes(financeData.knowsOverhead) && isYes(financeData.knowsLaborBurden) && isYes(financeData.knowsProjectRunway)) {
      strengths.push('Aware of Overhead and Labor Burden');
    }
    if (isYes(financeData.hasDebt)) weaknesses.push('Has Outstanding Debt');
    if (isYes(financeData.hasFinancialPartners)) strengths.push('Has Financial Partners');
    if (financeData.unencumberedCash) {
      const unencumberedCash = parseFloat(financeData.unencumberedCash || 0);
      const currentYearRevenue = parseFloat(salesData.currentYearProjection || salesData.revenueData?.currentYearProjection || 0);
      if (currentYearRevenue > 0) {
        const cashPercentage = (unencumberedCash / currentYearRevenue) * 100;
        if (cashPercentage > 10) strengths.push('Strong Unencumbered Cash Position');
      } else if (unencumberedCash > 0) strengths.push('Has Unencumbered Cash');
    }
    if (isYes(marketingData.runningDigitalAds)) strengths.push('Running Digital Advertising');
    else if (isNo(marketingData.runningDigitalAds)) opportunities.push('Implement Digital Advertising');
    const crm = techData.whatCRM || '';
    const crmLower = crm.toLowerCase();
    if (crmLower === 'none' || crmLower === '') opportunities.push('Implement or Upgrade CRM System');
    else if (crmLower.includes('jobtread')) strengths.push('Strong CRM - JobTread');
    else if (crmLower.includes('buildertrend')) strengths.push('Strong CRM - Buildertrend');
    // Name the actual CRM in optimization findings; fall back to generic "CRM" for None/Other.
    const crmDisplayName = (!crmLower || crmLower === 'none' || crmLower === 'other') ? 'CRM' : crm;
    const crmOpt = parseFloat(techData.crmOptimization || 0);
    if (crmOpt >= 75) strengths.push(`Strong ${crmDisplayName} Optimization`);
    else if (crmOpt > 0 && crmOpt < 75) {
      weaknesses.push(`Poor ${crmDisplayName} Optimization`);
      opportunities.push(`Improve ${crmDisplayName} Optimization`);
    }
    // Automations and AI tools are reported together.
    if (isYes(techData.hasAutomations) && isYes(techData.hasAITools)) {
      strengths.push('Has Automations and AI Tools');
    } else {
      weaknesses.push('No Automations or AI Tools');
      opportunities.push('Implement Automations and AI Tools');
    }

    return { strengths, weaknesses, opportunities, threats: [] };
  }, [operationsData, financeData, marketingData, techData, salesData]);

  const revenueAnalysis = useMemo(() => {
    const oneYearAgo = parseFloat(salesData.oneYearAgo || salesData.revenueData?.oneYearAgo || 0);
    const currentYear = parseFloat(salesData.currentYearProjection || salesData.revenueData?.currentYearProjection || 0);
    const nextYearGoal = parseFloat(salesData.nextYearGoal || salesData.revenueData?.nextYearGoal || salesData.goalsData?.annualRevenueTarget || 0);
    const calculatedYearOverYearGrowth = oneYearAgo > 0 ? ((currentYear - oneYearAgo) / oneYearAgo) * 100 : 0;
    const yearOverYearGrowth = calculatedYearOverYearGrowth;
    const goalGrowth = currentYear > 0 ? ((nextYearGoal - currentYear) / currentYear) * 100 : 0;
    const totalGrowth = oneYearAgo > 0 ? ((nextYearGoal - oneYearAgo) / oneYearAgo) * 100 : 0;
    const growthRate = oneYearAgo > 0 ? (currentYear / oneYearAgo) : 1;
    const projectedNextYear = nextYearGoal > 0 ? nextYearGoal : currentYear * growthRate;
    const projectedTwoYears = projectedNextYear * growthRate;
    const projectedThreeYears = projectedTwoYears * growthRate;
    return {
      yearOverYearGrowth: yearOverYearGrowth.toFixed(1),
      calculatedYearOverYearGrowth: calculatedYearOverYearGrowth.toFixed(1),
      goalGrowth: goalGrowth.toFixed(1),
      totalGrowth: totalGrowth.toFixed(1),
      growthRate: growthRate - 1,
      oneYearAgo,
      currentYear,
      nextYearGoal,
      projectedNextYear,
      projectedTwoYears,
      projectedThreeYears
    };
  }, [salesData]);

  const revenueData = useMemo(() => {
    const currentYearNum = new Date().getFullYear();
    const { oneYearAgo, currentYear, projectedNextYear, projectedTwoYears, projectedThreeYears } = revenueAnalysis;
    return {
      all: [
        { year: `${currentYearNum - 1}`, revenue: oneYearAgo, type: 'actual' },
        { year: `${currentYearNum}`, revenue: currentYear, type: 'actual' },
        { year: `${currentYearNum + 1}`, revenue: projectedNextYear, type: 'projected' },
        { year: `${currentYearNum + 2}`, revenue: projectedTwoYears, type: 'projected' },
        { year: `${currentYearNum + 3}`, revenue: projectedThreeYears, type: 'projected' }
      ],
      actual: [
        { year: `${currentYearNum - 1}`, revenue: oneYearAgo },
        { year: `${currentYearNum}`, revenue: currentYear }
      ],
      projected: [
        { year: `${currentYearNum}`, revenue: currentYear },
        { year: `${currentYearNum + 1}`, revenue: projectedNextYear },
        { year: `${currentYearNum + 2}`, revenue: projectedTwoYears },
        { year: `${currentYearNum + 3}`, revenue: projectedThreeYears }
      ]
    };
  }, [revenueAnalysis]);

  const displayGoals = useMemo(() => {
    const g = lead?.goals;
    if (!g) return [];
    if (Array.isArray(g) && g.length > 0) return g.map(c => ({ header: c.header || '', goal: c.goal || '' }));
    if (typeof g === 'object') {
      const cards = [];
      if (g.oneYear) cards.push({ header: '1 Year', goal: g.oneYear });
      if (g.threeYear) cards.push({ header: '3 Year', goal: g.threeYear });
      if (g.fiveYear) cards.push({ header: '5 Year', goal: g.fiveYear });
      return cards.length ? cards : [{ header: '1 Year', goal: '' }, { header: '3 Year', goal: '' }, { header: '5 Year', goal: '' }];
    }
    return [];
  }, [lead?.goals]);

  const displaySWOT = useMemo(() => {
    const customSWOT = lead?.customSWOT || {};
    if (customSWOT.strengths || customSWOT.weaknesses || customSWOT.opportunities || customSWOT.threats) {
      return {
        strengths: customSWOT.strengths || [],
        weaknesses: customSWOT.weaknesses || [],
        opportunities: customSWOT.opportunities || [],
        threats: customSWOT.threats || []
      };
    }
    return {
      strengths: swotData.strengths || [],
      weaknesses: swotData.weaknesses || [],
      opportunities: swotData.opportunities || [],
      threats: swotData.threats || []
    };
  }, [lead?.customSWOT, swotData]);

  const displayLevers = useMemo(() => {
    const ll = lead?.immediateLevers;
    if (!ll) return [];
    if (Array.isArray(ll) && ll.length > 0) { return ll.map(c => ({ emoji: c.emoji || '', header: c.header || '', description: c.description || '', department: c.department || '' })); }
    if (typeof ll === 'object' && !Array.isArray(ll)) {
      const depts = ['sales', 'operations', 'finance', 'marketing', 'technology'];
      const cards = [];
      depts.forEach(dept => {
        const items = Array.isArray(ll[dept]) ? ll[dept] : (ll[dept]?.text || ll[dept]?.emoji ? [ll[dept]] : []);
        items.forEach(item => cards.push({
          emoji: item.emoji || '',
          header: dept.charAt(0).toUpperCase() + dept.slice(1),
          description: item.text || item.description || '',
          department: dept
        }));
      });
      return cards;
    }
    return [];
  }, [lead?.immediateLevers]);

  const displayImplementationResults = useMemo(() => {
    return Array.isArray(lead?.implementationResults) ? [...lead.implementationResults] : [];
  }, [lead?.implementationResults]);

  const closeRatio = salesData.closeRatio || 0;
  const closeRatioStatus = closeRatio < 30 ? 'weak' : closeRatio < 50 ? 'average' : 'strong';

  const renderStarRating = (rating, label) => (
    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
      <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-center">{label}</h4>
      {rating > 0 ? (
        <div className="flex items-center justify-center space-x-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star key={star} className={`w-6 h-6 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 dark:text-gray-600'}`} />
          ))}
          <span className="ml-2 text-gray-600 dark:text-gray-300 font-medium">{rating}/5</span>
        </div>
      ) : (
        <p className="text-gray-400 dark:text-gray-500 italic text-center">Not available</p>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <StartSidebar />
        <div className="max-w-7xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600 dark:text-gray-400">Loading assessment analysis...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <StartSidebar />
        <div className="max-w-7xl mx-auto">
          <BackButton to="/start" />
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mt-4">
            <div className="text-center py-12">
              <p className="text-red-600 dark:text-red-400">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Company logo (never the contact's headshot) — same source the CRM account/lead cards use.
  const companyLogoUrl = getCrmCompanyLogoDisplayUrl(lead);

  return (
    <div className="bg-white dark:bg-gray-950 min-h-screen p-4 relative">
      <StartSidebar />
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <BackButton to="/start" />
            <div className="flex-1 flex flex-col items-center">
              {companyLogoUrl && (
                <div className="max-w-xs mb-3">
                  <img src={companyLogoUrl} alt={`${lead?.company || 'Company'} logo`} className="w-full h-auto object-contain rounded-lg shadow-lg" />
                </div>
              )}
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white text-center">
                {lead ? `${lead.company || 'N/A'} Analysis` : 'Assessment Analysis'}
              </h1>
            </div>
            <div className="w-20" />
          </div>
        </div>

        <div>
          {/* Company Rating */}
          <div className="mb-8">
            <div className="grid grid-cols-1 gap-4 mb-6">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-4 text-center">Company Rating</h4>
                <div className="text-center">
                  {(() => {
                    const ratings = [bbaRating, operationsBBARating, financeBBARating, marketingBBARating, techBBARating].filter(r => r > 0);
                    const avgRating = ratings.length > 0 ? ratings.reduce((sum, r) => sum + r, 0) / ratings.length : 0;
                    const companyRating = avgRating * 20;
                    return <p className="text-5xl font-bold text-gray-900 dark:text-white">{companyRating.toFixed(0)}/100</p>;
                  })()}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {renderStarRating(ownerRating, 'Sales')}
              {renderStarRating(operationsOwnerRating, 'Operations')}
              {renderStarRating(financeOwnerRating, 'Finance')}
              {renderStarRating(marketingOwnerRating, 'Marketing')}
              {renderStarRating(techOwnerRating, 'Technology')}
            </div>
          </div>

          {/* Close Ratio, GPM, NPM */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 flex flex-col h-full">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4 text-center">Close Ratio</h3>
              {closeRatio > 0 ? (
                <div className="flex flex-col h-full">
                  <div className="text-center">
                    <p className={`text-3xl font-bold ${closeRatioStatus === 'weak' ? 'text-red-600 dark:text-red-400' : closeRatioStatus === 'average' ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'}`}>
                      {closeRatio.toFixed(1)}%
                    </p>
                  </div>
                  <div className="mt-4">
                    <div className="relative h-6 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                      <div className="absolute left-0 top-0 h-full bg-red-500" style={{ width: '30%' }}></div>
                      <div className="absolute left-0 top-0 h-full bg-yellow-500" style={{ left: '30%', width: '20%' }}></div>
                      <div className="absolute left-0 top-0 h-full bg-green-500" style={{ left: '50%', width: '50%' }}></div>
                      <div className="absolute top-0 h-full w-1 bg-gray-900 dark:bg-white shadow-lg z-10" style={{ left: `${Math.min(100, closeRatio)}%`, transform: 'translateX(-50%)' }}></div>
                    </div>
                    <div className="relative mt-1 text-xs text-gray-600 dark:text-gray-400">
                      <span className="absolute left-0">0%</span>
                      <span className="absolute left-[30%] transform -translate-x-1/2">30%</span>
                      <span className="absolute left-[50%] transform -translate-x-1/2">50%</span>
                      <span className="absolute right-0">100%</span>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-400 dark:text-gray-500 italic text-center">No data available</p>
              )}
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 flex flex-col h-full">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4 text-center">Gross Profit Margin</h3>
              {financeData.grossProfitMargin ? (
                (() => {
                  const gpm = parseFloat(financeData.grossProfitMargin || 0);
                  const status = gpm < 30 ? 'poor' : gpm <= 40 ? 'good' : 'great';
                  return (
                    <div className="flex flex-col h-full">
                      <div className="text-center">
                        <p className={`text-3xl font-bold ${status === 'poor' ? 'text-red-600 dark:text-red-400' : status === 'good' ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'}`}>{gpm.toFixed(1)}%</p>
                      </div>
                      <div className="mt-4">
                        <div className="relative h-6 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                          <div className="absolute left-0 top-0 h-full bg-red-500" style={{ width: '30%' }}></div>
                          <div className="absolute left-0 top-0 h-full bg-yellow-500" style={{ left: '30%', width: '10%' }}></div>
                          <div className="absolute left-0 top-0 h-full bg-green-500" style={{ left: '40%', width: '60%' }}></div>
                          <div className="absolute top-0 h-full w-1 bg-gray-900 dark:bg-white shadow-lg z-10" style={{ left: `${Math.min(100, gpm)}%`, transform: 'translateX(-50%)' }}></div>
                        </div>
                        <div className="relative mt-1 text-xs text-gray-600 dark:text-gray-400">
                          <span className="absolute left-0">0%</span>
                          <span className="absolute left-[30%] transform -translate-x-1/2">30%</span>
                          <span className="absolute left-[40%] transform -translate-x-1/2">40%</span>
                          <span className="absolute right-0">100%</span>
                        </div>
                      </div>
                    </div>
                  );
                })()
              ) : (
                <p className="text-gray-400 dark:text-gray-500 italic text-center">No data available</p>
              )}
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 flex flex-col h-full">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4 text-center">Net Profit Margin</h3>
              {financeData.netProfitMargin ? (
                (() => {
                  const npm = parseFloat(financeData.netProfitMargin || 0);
                  const status = npm < 5 ? 'poor' : npm <= 10 ? 'good' : 'great';
                  return (
                    <div className="flex flex-col h-full">
                      <div className="text-center">
                        <p className={`text-3xl font-bold ${status === 'poor' ? 'text-red-600 dark:text-red-400' : status === 'good' ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'}`}>{npm.toFixed(1)}%</p>
                      </div>
                      <div className="mt-4">
                        <div className="relative h-6 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                          <div className="absolute left-0 top-0 h-full bg-red-500" style={{ width: '10%' }}></div>
                          <div className="absolute left-0 top-0 h-full bg-yellow-500" style={{ left: '10%', width: '10%' }}></div>
                          <div className="absolute left-0 top-0 h-full bg-green-500" style={{ left: '20%', width: '80%' }}></div>
                          <div className="absolute top-0 h-full w-1 bg-gray-900 dark:bg-white shadow-lg z-10" style={{ left: `${Math.min(100, (npm / 50) * 100)}%`, transform: 'translateX(-50%)' }}></div>
                        </div>
                        <div className="relative mt-1 text-xs text-gray-600 dark:text-gray-400">
                          <span className="absolute left-0">0%</span>
                          <span className="absolute left-[10%] transform -translate-x-1/2">5%</span>
                          <span className="absolute left-[20%] transform -translate-x-1/2">10%</span>
                          <span className="absolute right-0">50%</span>
                        </div>
                      </div>
                    </div>
                  );
                })()
              ) : (
                <p className="text-gray-400 dark:text-gray-500 italic text-center">No data available</p>
              )}
            </div>
          </div>

          {/* Revenue Trend */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white text-center">Revenue Trend</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                <h3 className="font-medium text-gray-600 dark:text-gray-400 text-sm mb-1">Previous Year</h3>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{revenueAnalysis.oneYearAgo > 0 ? `$${revenueAnalysis.oneYearAgo.toLocaleString()}` : '—'}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                <h3 className="font-medium text-gray-600 dark:text-gray-400 text-sm mb-1">Current Year</h3>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{revenueAnalysis.currentYear > 0 ? `$${revenueAnalysis.currentYear.toLocaleString()}` : '—'}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                <h3 className="font-medium text-gray-600 dark:text-gray-400 text-sm mb-1">Projected Next Year</h3>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{revenueAnalysis.projectedNextYear > 0 ? `$${revenueAnalysis.projectedNextYear.toLocaleString()}` : '—'}</p>
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 mb-6">
              {revenueData.all.some(d => d.revenue > 0) ? (
                <div>
                  <div className="mb-4 flex flex-col items-center gap-3">
                    <div className="flex items-center justify-center gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-0.5 bg-blue-600"></div>
                        <span className="text-gray-600 dark:text-gray-400">Actual</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-0.5 bg-blue-600" style={{ borderTop: '2px dashed #3B82F6' }}></div>
                        <span className="text-gray-600 dark:text-gray-400">Projected</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Growth Rate:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{revenueAnalysis.yearOverYearGrowth}%</span>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart>
                      <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                      <XAxis dataKey="year" stroke={darkMode ? '#9ca3af' : '#6b7280'} type="category" allowDuplicatedCategory={false} />
                      <YAxis stroke={darkMode ? '#9ca3af' : '#6b7280'} tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(value) => `$${parseInt(value).toLocaleString()}`} contentStyle={{ backgroundColor: darkMode ? '#1f2937' : '#ffffff', border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`, color: darkMode ? '#f3f4f6' : '#111827' }} />
                      <Legend />
                      <Area type="monotone" data={revenueData.actual} dataKey="revenue" stroke="#3B82F6" strokeWidth={2} fill="#3B82F6" fillOpacity={0.6} name="Actual Revenue" dot={{ fill: '#3B82F6', r: 5 }} connectNulls={false}>
                        <LabelList dataKey="revenue" position="top" formatter={(value, entry, index) => {
                          try {
                            if (value === undefined || value === null) return '';
                            const currentYearNum = new Date().getFullYear();
                            const dataPoint = revenueData.actual[index];
                            if (dataPoint && dataPoint.year === `${currentYearNum}`) return `$${(value / 1000).toFixed(0)}k`;
                            return '';
                          } catch (e) { return ''; }
                        }} style={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: '12px', fontWeight: '500' }} />
                      </Area>
                      <Area type="monotone" data={revenueData.projected} dataKey="revenue" stroke="#3B82F6" strokeWidth={2} strokeDasharray="5 5" fill="#3B82F6" fillOpacity={0.3} name="Projected Revenue" dot={{ fill: '#3B82F6', r: 5, stroke: '#fff', strokeWidth: 2 }} connectNulls={false}>
                        <LabelList dataKey="revenue" position="top" formatter={(value, entry, index) => {
                          try {
                            if (value === undefined || value === null) return '';
                            const currentYearNum = new Date().getFullYear();
                            const dataPoint = revenueData.projected[index];
                            if (dataPoint && dataPoint.year === `${currentYearNum + 1}`) return `$${(value / 1000).toFixed(0)}k`;
                            return '';
                          } catch (e) { return ''; }
                        }} style={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: '12px', fontWeight: '500' }} />
                      </Area>
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-gray-400 dark:text-gray-500 italic text-center py-12">No revenue data available</p>
              )}
            </div>
          </div>

          {/* Goals */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center mb-4">Goals</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {displayGoals.length === 0 && <p className="col-span-full text-center text-gray-400 dark:text-gray-500 italic py-8">No goals yet.</p>}
              {displayGoals.map((card, idx) => (
                <div key={idx} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="text-center">
                    <h3 className="font-medium text-gray-600 dark:text-gray-400 text-sm mb-3">{card.header || '—'}</h3>
                    <p className="text-xl font-bold text-gray-900 dark:text-white whitespace-pre-wrap">{card.goal || <span className="italic text-gray-400 dark:text-gray-500 font-normal">No goal set</span>}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* SWOT */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center mb-4">SWOT Analysis</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6 border-2 border-green-500 dark:border-green-400">
                <h3 className="font-semibold text-green-800 dark:text-green-300 text-lg text-center mb-4">Strengths</h3>
                {displaySWOT.strengths && displaySWOT.strengths.length > 0 ? (
                  <ul className="space-y-2">
                    {displaySWOT.strengths.map((strength, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{strength}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 italic text-center">N/A</p>
                )}
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-6 border-2 border-red-500 dark:border-red-400">
                <h3 className="font-semibold text-red-800 dark:text-red-300 text-lg text-center mb-4">Weaknesses</h3>
                {displaySWOT.weaknesses && displaySWOT.weaknesses.length > 0 ? (
                  <ul className="space-y-2">
                    {displaySWOT.weaknesses.map((weakness, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{weakness}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 italic text-center">N/A</p>
                )}
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border-2 border-blue-500 dark:border-blue-400">
                <h3 className="font-semibold text-blue-800 dark:text-blue-300 text-lg text-center mb-4">Opportunities</h3>
                {displaySWOT.opportunities && displaySWOT.opportunities.length > 0 ? (
                  <ul className="space-y-2">
                    {displaySWOT.opportunities.map((opportunity, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{opportunity}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 italic text-center">N/A</p>
                )}
              </div>
            </div>
          </div>

          {/* Key Implementations */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center mb-4">Key Implementations</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {displayLevers.length === 0 && <p className="col-span-full text-center text-gray-400 dark:text-gray-500 italic py-8">No cards yet.</p>}
              {displayLevers.map((card, idx) => (
                <div key={idx} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="text-center">
                    {card.emoji && <span className="text-3xl block mb-2">{card.emoji}</span>}
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{card.header || '—'}</h3>
                    {card.department && (
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium mb-2 ${departmentPillClasses[LEVER_DEPARTMENTS.find(d => d.id === card.department)?.color || 'red']}`}>
                        {LEVER_DEPARTMENTS.find(d => d.id === card.department)?.name || card.department}
                      </span>
                    )}
                    {card.description && <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{card.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Implementation Results */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center mb-4">Implementation Results</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 flex flex-col items-center justify-center">
                {companyLogoUrl && (
                  <div className="max-w-xs mb-4">
                    <img src={companyLogoUrl} alt={`${lead?.company || 'Company'} logo`} className="w-full h-auto object-contain" />
                  </div>
                )}
                <h3 className="text-xl font-bold text-gray-900 dark:text-white text-center">{lead?.company || 'Company'} is...</h3>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                {displayImplementationResults.length === 0 && <p className="text-center text-gray-400 dark:text-gray-500 italic py-4">No results yet.</p>}
                <div className="space-y-4">
                  {displayImplementationResults.map((text, idx) => (
                    <div key={idx} className="border-b border-gray-300 dark:border-gray-600 pb-3 last:border-b-0">
                      <p className="text-xl font-bold text-gray-900 dark:text-white">{text || <span className="italic text-gray-400 dark:text-gray-500 font-normal">—</span>}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Suggested Program - from Proposed Program (proposal tab in LeadOverview) */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center mb-4">Suggested Program</h2>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
              {lead?.proposedProgram ? (
                <ProgramDetail
                  embedded={true}
                  showGoToProgramsButton={true}
                  selectedProgramProp={{
                    name: lead.proposedProgram.programName?.replace(/\s*Program\s*$/, '') || 'Program',
                    features: lead.proposedProgram.features || {},
                    pricePerMonth: lead.proposedProgram.totalPricePerMonth ?? 0,
                    minimumTerm: lead.proposedProgram.programMinimumTerm ?? 12
                  }}
                  selectedTierProp={lead.proposedProgram.selectedTier || ''}
                  billingCycleProp={lead.proposedProgram.billingCycle || 'monthly'}
                  proposalMode={true}
                  hideActionButton={true}
                  hideProgramDropdown={true}
                  leadIdProp={resolvedLeadId ?? leadId}
                  confirmMode={true}
                  proposedProgramData={{ ...lead.proposedProgram, isProgramConfirmed: true }}
                  initialSelectedDeliverables={lead.proposedProgram.selectedDeliverables ? new Set(lead.proposedProgram.selectedDeliverables) : null}
                  initialDeliverableQuantities={lead.proposedProgram.deliverableQuantities || null}
                />
              ) : (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">Your proposed program will appear here once your BBA team has confirmed it in Assessment Analysis.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentAnalysisOverview;
